import React from 'react';
import { Filter, X } from 'lucide-react';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const FilterSystem = ({ filters, onFiltersChange }) => {
  const communeOptions = [
    { value: '', label: 'Toutes les communes' },
    { value: 'cocody', label: 'Cocody' },
    { value: 'plateau', label: 'Plateau' },
    { value: 'marcory', label: 'Marcory' },
    { value: 'yopougon', label: 'Yopougon' },
    { value: 'adjame', label: 'Adjamé' },
    { value: 'treichville', label: 'Treichville' },
    { value: 'koumassi', label: 'Koumassi' },
    { value: 'port-bouet', label: 'Port-Bouët' },
    { value: 'abobo', label: 'Abobo' },
    { value: 'attécoubé', label: 'Attécoubé' }
  ];

  const propertyTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const priceRangeOptions = [
    { value: '', label: 'Tous les prix' },
    { value: '0-50000000', label: '0 - 50M FCFA' },
    { value: '50000000-100000000', label: '50M - 100M FCFA' },
    { value: '100000000-200000000', label: '100M - 200M FCFA' },
    { value: '200000000-500000000', label: '200M - 500M FCFA' },
    { value: '500000000-', label: '500M+ FCFA' }
  ];

  const tourDurationOptions = [
    { value: '', label: 'Toutes les durées' },
    { value: 'short', label: 'Court (≤ 5 min)' },
    { value: 'medium', label: 'Moyen (5-10 min)' },
    { value: 'long', label: 'Long (> 10 min)' }
  ];

  const handleFilterChange = (field, value) => {
    onFiltersChange?.({
      ...filters,
      [field]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange?.({
      commune: '',
      propertyType: '',
      priceRange: '',
      tourDuration: ''
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground text-sm">Filtres</h3>
        </div>
        
        {hasActiveFilters && (
          <Button
            size="xs"
            variant="ghost"
            onClick={clearFilters}
            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1"
          >
            <X className="w-3.5 h-3.5" />
            Effacer
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Commune Filter */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5">
            Commune
          </label>
          <Select
            options={communeOptions}
            value={filters?.commune}
            onChange={(value) => handleFilterChange('commune', value)}
            placeholder="Sélectionner une commune"
          />
        </div>

        {/* Property Type Filter */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5">
            Type de propriété
          </label>
          <Select
            options={propertyTypeOptions}
            value={filters?.propertyType}
            onChange={(value) => handleFilterChange('propertyType', value)}
            placeholder="Sélectionner un type"
          />
        </div>

        {/* Price Range Filter */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5">
            Gamme de prix
          </label>
          <Select
            options={priceRangeOptions}
            value={filters?.priceRange}
            onChange={(value) => handleFilterChange('priceRange', value)}
            placeholder="Sélectionner un prix"
          />
        </div>

        {/* Tour Duration Filter */}
        <div>
          <label className="block text-xs font-medium text-foreground mb-1.5">
            Durée de la visite
          </label>
          <Select
            options={tourDurationOptions}
            value={filters?.tourDuration}
            onChange={(value) => handleFilterChange('tourDuration', value)}
            placeholder="Sélectionner une durée"
          />
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground mb-2">Filtres actifs :</p>
            <div className="flex flex-wrap gap-2">
              {filters?.commune && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-accent-foreground text-xs rounded-md border border-border">
                  {communeOptions?.find(opt => opt?.value === filters?.commune)?.label}
                  <button
                    onClick={() => handleFilterChange('commune', '')}
                    className="hover:text-destructive rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {filters?.propertyType && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-accent-foreground text-xs rounded-md border border-border">
                  {propertyTypeOptions?.find(opt => opt?.value === filters?.propertyType)?.label}
                  <button
                    onClick={() => handleFilterChange('propertyType', '')}
                    className="hover:text-destructive rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {filters?.priceRange && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-accent-foreground text-xs rounded-md border border-border">
                  {priceRangeOptions?.find(opt => opt?.value === filters?.priceRange)?.label}
                  <button
                    onClick={() => handleFilterChange('priceRange', '')}
                    className="hover:text-destructive rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              
              {filters?.tourDuration && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent text-accent-foreground text-xs rounded-md border border-border">
                  {tourDurationOptions?.find(opt => opt?.value === filters?.tourDuration)?.label}
                  <button
                    onClick={() => handleFilterChange('tourDuration', '')}
                    className="hover:text-destructive rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSystem;